import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi } from '@/services/auth.api';
import { setSentryUser } from '@/lib/sentry';
import { trackAuthEvent } from '@/lib/telemetry';
import { getCurrentUserId } from '@/features/auth/currentUser';

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  logout: () => void;
  checkAuth: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  isLoading: true,
  error: null,
  logout: () => {},
  checkAuth: () => {},
});

export function useAuth() {
  return useContext(AuthContext);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const checkAuth = () => {
    const token = localStorage.getItem('auth_token');
    if (token) {
      setIsAuthenticated(true);
      setError(null);
      // Restore user identity in Sentry so subsequent events carry it.
      const id = getCurrentUserId();
      const email = localStorage.getItem('user_email') ?? undefined;
      if (id) {
        setSentryUser({ id, email });
      }
    } else {
      setIsAuthenticated(false);
      setError('No authentication token available');
      setSentryUser(null);
    }
    setIsLoading(false);
  };

  const logout = () => {
    authApi.logout();
    setIsAuthenticated(false);
    setError(null);
    setSentryUser(null);
    trackAuthEvent('logout');
  };

  useEffect(() => {
    async function initAuth() {
      try {
        const existingToken = localStorage.getItem('auth_token');

        if (existingToken) {
          setIsAuthenticated(true);
          setIsLoading(false);
          // Restore user identity in Sentry for this session.
          const id = getCurrentUserId();
          const email = localStorage.getItem('user_email') ?? undefined;
          if (id) {
            setSentryUser({ id, email });
          }
          trackAuthEvent('session-restored', email ? { email } : undefined);
          return;
        }

        const devAutoLogin = import.meta.env.VITE_DEV_AUTO_LOGIN === 'true';

        if (devAutoLogin) {
          await authApi.login('admin@demo.local', 'password123', 'demo');
          setIsAuthenticated(true);
        } else {
          setError('No authentication token available');
        }
      } catch (err) {
        console.error('Auto-login failed:', err);
        setError('Authentication failed');
      } finally {
        setIsLoading(false);
      }
    }

    initAuth();
  }, []);

  return (
    <AuthContext.Provider value={{ isAuthenticated, isLoading, error, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}
